'use client'

import { createContext, useContext, useState, useCallback, useRef } from 'react'
import { undoSoftDelete } from '@/lib/adminSoftDelete'
import { getAdminHeaders } from '@/lib/artworkApi'

const UndoToastContext = createContext(null)

export function useUndoToast() {
  const ctx = useContext(UndoToastContext)
  if (!ctx) throw new Error('useUndoToast must be used inside UndoToastProvider')
  return ctx
}

/**
 * UndoToastProvider
 *
 * Wraps the admin layout and provides showUndoToast({ table, id, label, onRestored }).
 * Shows a 5-second undo toast stack (max 3 visible) after a soft delete.
 * Clicking "Restore" calls POST /api/admin/undo to restore the row.
 */
export default function UndoToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timersRef = useRef(new Map())

  const dismiss = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id))
    const timer = timersRef.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }
  }, [])

  const showUndoToast = useCallback(
    ({ table, id, label, onRestored }) => {
      const toastId = `${table}-${id}-${Date.now()}`
      const newToast = { id: toastId, table, rowId: id, label, onRestored }
      setToasts((t) => [...t, newToast].slice(-3))
      const timer = setTimeout(() => dismiss(toastId), 5000)
      timersRef.current.set(toastId, timer)
    },
    [dismiss]
  )

  const restore = useCallback(
    async (toast) => {
      try {
        const result = await undoSoftDelete({
          table: toast.table,
          id: toast.rowId,
          getAdminHeaders,
        })
        toast.onRestored?.(result.restored)
        dismiss(toast.id)
      } catch (e) {
        console.error('Undo failed', e)
      }
    },
    [dismiss]
  )

  return (
    <UndoToastContext.Provider value={{ showUndoToast }}>
      {children}
      <div className="admin-undo-toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className="admin-undo-toast" role="status">
            <span className="admin-undo-toast__label">{t.label}</span>
            <button
              type="button"
              className="admin-undo-toast__btn"
              onClick={() => restore(t)}
            >
              還原
            </button>
          </div>
        ))}
      </div>
    </UndoToastContext.Provider>
  )
}
